import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { ValidationError } from 'yup';
import {
  EmergencyContactValues,
  emergencyContactSettingsSchema,
  GetWaiversData,
  EmergencyContactEnabledField,
} from '../../../types/waivers';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import '../../form/form.css';
import MultiSwitchInput from '../../form/base/MultiSwitchInput';

interface Props {
  onSuccess?: () => void;
  draft: GetWaiversData | null | undefined;
}

const getEnabledFields = (draft: GetWaiversData | null | undefined) => {
  const fields: EmergencyContactEnabledField[] = [];
  if (!draft) return fields;

  if (draft.emergencyContactSettings?.requireName) fields.push('name');
  if (draft.emergencyContactSettings?.requirePhone) fields.push('phone');
  if (draft.emergencyContactSettings?.requireEmail) fields.push('email');
  if (draft.emergencyContactSettings?.requireAddress) fields.push('address');

  return fields;
};

export default function EmergencyContactForm({ onSuccess, draft }: Props) {
  const [formError, setFormError] = React.useState('');

  const { mutation, isPending } = useUpdateWaiver({ draft });

  const [initialFormState] = React.useState<EmergencyContactValues>({
    enabledFields: getEnabledFields(draft),
  });

  const onSubmit = async (values: EmergencyContactValues) => {
    if (!draft?.businessId || isPending) return;
    try {
      setFormError('');

      const emergencyContactSettings: Parameters<
        typeof mutation.mutateAsync
      >[0]['emergencyContactSettings'] = {
        requireName: values.enabledFields.includes('name'),
        requirePhone: values.enabledFields.includes('phone'),
        requireEmail: values.enabledFields.includes('email'),
        requireAddress: values.enabledFields.includes('address'),
      };

      const response = await mutation.mutateAsync({
        emergencyContactSettings,
      });

      if (response) {
        toast.success('Emergency contact settings saved successfully');
        onSuccess?.();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        setFormError(error.message);
      } else {
        toast.error('Failed to update emergency contact settings');
      }
    }
  };

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={emergencyContactSettingsSchema}
        onSubmit={onSubmit}>
        {({ isSubmitting, isValid, isValidating, dirty }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}
            <Stack width="100%" spacing={2.5}>
              <MultiSwitchInput
                name="enabledFields"
                label="Select which fields you'd like to require clients to fill out:"
                options={[
                  {
                    title: 'Name',
                    label:
                      "Require the client to fill out their emergency contact's name",
                    value: 'name',
                  },
                  {
                    title: 'Phone',
                    label:
                      "Require the client to fill out their emergency contact's phone number",
                    value: 'phone',
                  },
                  {
                    title: 'Email',
                    label:
                      "Require the client to fill out their emergency contact's email address",
                    value: 'email',
                  },
                  {
                    title: 'Address',
                    label:
                      "Require the client to fill out their emergency contact's address",
                    value: 'address',
                  },
                ]}
                required
              />
            </Stack>

            <LoadingButton
              type="submit"
              disabled={
                !isValid || isSubmitting || !dirty || isValidating || isPending
              }
              variant="contained"
              className="primary"
              loading={isSubmitting}>
              <span>Save</span>
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
